/**
 * Dues schedules and assessments.
 *
 * Both screens are built around the same idea: the board decides a
 * number and a date, and the software's job is to hold both without
 * losing either. So the dues page shows what is in force AND what is
 * scheduled, and the assessment page insists on a preview you can read
 * before anything is charged to anybody.
 */

import { Fragment, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { money, date, parseMoney, titleCase, type CurrencyInfo } from '../lib/format.ts';
import {
  BylawNotices, Card, Empty, ErrorBanner, Loading, Money, Status, Tile,
} from '../components/ui.tsx';

/* ================================================================== */
/* Dues schedule                                                       */
/* ================================================================== */

interface Rate {
  id: string;
  dues_amount_minor: number;
  billing_frequency: string;
  minimum_amount_minor: number;
  effective_from: string;
  effective_to: string | null;
  approved_on: string | null;
  note: string | null;
}

interface ScheduledCategory {
  category_id: string;
  code: string;
  name: string;
  rates: Rate[];
  current: Rate | null;
  next: Rate | null;
}

export function DuesSchedulePage() {
  const { currency, atLeast } = useSession();
  const canEdit = atLeast('controller');
  const schedule = useApi<{ categories: ScheduledCategory[] }>('/v1/dues-schedule');
  const [editing, setEditing] = useState<ScheduledCategory | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  if (schedule.loading) return <Loading rows={6} />;

  return (
    <>
      {/* Whatever the club's own by-laws say about raising dues. DUES-2
          refuses a rise past the cap; this shows the rule to whoever is
          about to try, which is a better order to learn it in. */}
      <BylawNotices surface="dues.schedule" />

      <div className="row" style={{ marginBottom: 16 }}>
        <div>
          <h1>Dues</h1>
          <div className="muted small">
            What each class pays, and from when. A run for any period bills the rate that was
            in force then.
          </div>
        </div>
      </div>

      <ErrorBanner error={schedule.error} />

      {editing && (
        <SetRateForm
          category={editing}
          currency={currency}
          onDone={() => {
            setEditing(null);
            schedule.reload();
          }}
          onCancel={() => setEditing(null)}
        />
      )}

      <Card flush>
        <table className="data">
          <thead>
            <tr>
              <th>Class</th>
              <th className="num">Now</th>
              <th>Billed</th>
              <th className="num">Scheduled</th>
              <th>From</th>
              {canEdit && <th />}
            </tr>
          </thead>
          <tbody>
            {(schedule.data?.categories ?? []).map((c) => (
              <Fragment key={c.category_id}>
                <tr>
                  <td>
                    {c.name}
                    {c.rates.length > 1 && (
                      <button
                        className="secondary small"
                        style={{ marginLeft: 8 }}
                        onClick={() => setExpanded(expanded === c.category_id ? null : c.category_id)}
                      >
                        {c.rates.length} rates
                      </button>
                    )}
                  </td>
                  <td className="num">
                    {c.current ? money(c.current.dues_amount_minor, currency) : '—'}
                  </td>
                  <td className="muted">
                    {c.current ? titleCase(c.current.billing_frequency) : '—'}
                  </td>
                  <td className="num">
                    {c.next ? (
                      <strong>{money(c.next.dues_amount_minor, currency)}</strong>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td className="muted">{c.next ? date(c.next.effective_from) : ''}</td>
                  {canEdit && (
                    <td className="num">
                      <button className="secondary small" onClick={() => setEditing(c)}>
                        Set a rate
                      </button>
                    </td>
                  )}
                </tr>
                {expanded === c.category_id &&
                  c.rates.map((r) => (
                    <tr key={r.id} style={{ background: 'var(--surface-sunk)' }}>
                      <td className="small" style={{ paddingLeft: 28 }}>
                        {date(r.effective_from)} —{' '}
                        {r.effective_to ? date(r.effective_to) : 'in force'}
                        {r.approved_on && (
                          <span className="muted"> · approved {date(r.approved_on)}</span>
                        )}
                      </td>
                      <td className="num small">{money(r.dues_amount_minor, currency)}</td>
                      <td className="muted small" colSpan={3}>
                        {r.note ?? ''}
                      </td>
                      {canEdit && <td />}
                    </tr>
                  ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </Card>
    </>
  );
}

function SetRateForm({
  category,
  currency,
  onDone,
  onCancel,
}: {
  category: ScheduledCategory;
  currency: CurrencyInfo;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [amount, setAmount] = useState('');
  const [from, setFrom] = useState('');
  const [approvedOn, setApprovedOn] = useState('');
  const [note, setNote] = useState('');

  const save = useMutation(async (body: Record<string, unknown>) =>
    api<{ note: string | null }>('/v1/dues-schedule', { method: 'POST', body }),
  );

  const minor = parseMoney(amount, currency);

  return (
    <Card title={`Set the rate for ${category.name}`}>
      <ErrorBanner error={save.error} />
      <div className="row">
        <label>
          <span className="muted small">Amount</span>
          <input value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
        </label>
        <label>
          <span className="muted small">In force from</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label>
          <span className="muted small">Board approved</span>
          <input type="date" value={approvedOn} onChange={(e) => setApprovedOn(e.target.value)} />
        </label>
        <label style={{ flex: 1 }}>
          <span className="muted small">Note</span>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Board meeting, November 2026"
          />
        </label>
      </div>
      <p className="muted small" style={{ marginTop: 8 }}>
        The rate currently in force is closed the day before this one starts. Nothing already
        billed changes, and a run for an earlier period will still bill the earlier rate.
      </p>
      <div className="row" style={{ marginTop: 12 }}>
        <button
          className="gold"
          disabled={save.busy || minor === null || !from}
          onClick={async () => {
            const r = await save.run({
              category_id: category.category_id,
              dues_amount_minor: minor,
              effective_from: from,
              ...(approvedOn ? { approved_on: approvedOn } : {}),
              ...(note ? { note } : {}),
            });
            if (r) onDone();
          }}
        >
          {save.busy ? 'Saving…' : 'Set rate'}
        </button>
        <button className="secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </Card>
  );
}

/* ================================================================== */
/* Assessments                                                         */
/* ================================================================== */

interface AssessmentRow {
  id: string;
  code: string;
  name: string;
  kind: string;
  treatment: string;
  levy_date: string;
  due_date: string | null;
  status: string;
  member_count: number;
  total_minor: number;
  outstanding_minor: number;
  active_plans: number;
}

export function AssessmentsPage() {
  const { currency, atLeast } = useSession();
  const list = useApi<{ assessments: AssessmentRow[] }>('/v1/assessments');
  const [creating, setCreating] = useState(false);

  return (
    <>
      {/* DUES-3 refuses a levy the members have not carried. This is the
          same rule stated before it bites, which is where a club would
          rather read it. */}
      <BylawNotices surface="assessment.levy" />

      <div className="row" style={{ marginBottom: 16 }}>
        <div>
          <h1>Assessments</h1>
          <div className="muted small">
            Capital and special levies. The whole amount lands on the member's account on the
            day the board votes; how they pay it is a separate decision.
          </div>
        </div>
        <div className="spacer" />
        {atLeast('controller') && (
          <button className="gold" onClick={() => setCreating(true)}>
            New assessment
          </button>
        )}
      </div>

      <ErrorBanner error={list.error} />

      {creating && (
        <NewAssessmentForm
          currency={currency}
          onDone={() => {
            setCreating(false);
            list.reload();
          }}
          onCancel={() => setCreating(false)}
        />
      )}

      <Card flush>
        {list.loading ? (
          <div style={{ padding: 16 }}>
            <Loading rows={4} />
          </div>
        ) : (list.data?.assessments ?? []).length === 0 ? (
          <Empty>No assessments yet.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Assessment</th>
                <th>Levied</th>
                <th>Treatment</th>
                <th>Status</th>
                <th className="num">Members</th>
                <th className="num">Total</th>
                <th className="num">Outstanding</th>
              </tr>
            </thead>
            <tbody>
              {(list.data?.assessments ?? []).map((a) => (
                <tr key={a.id}>
                  <td>
                    <Link to={`/finance/assessments/${a.id}`}>{a.name}</Link>
                    <span className="muted small mono"> {a.code}</span>
                  </td>
                  <td className="muted">{date(a.levy_date)}</td>
                  <td className="muted">
                    {titleCase(a.kind)} · {titleCase(a.treatment)}
                  </td>
                  <td>
                    {a.status === 'committed' ? (
                      <Status tone="good">Levied</Status>
                    ) : a.status === 'reversed' ? (
                      <Status tone="neutral">Reversed</Status>
                    ) : (
                      <Status tone="warning">Preview</Status>
                    )}
                  </td>
                  <td className="num">{a.member_count}</td>
                  <td className="num">{money(a.total_minor, currency)}</td>
                  <td className="num">
                    {a.status === 'committed' ? money(a.outstanding_minor, currency) : '—'}
                    {a.active_plans > 0 && (
                      <div className="muted small">{a.active_plans} on plans</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

function NewAssessmentForm({
  currency,
  onDone,
  onCancel,
}: {
  currency: CurrencyInfo;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [kind, setKind] = useState('capital');
  const [treatment, setTreatment] = useState('equity');
  const [levyDate, setLevyDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState('');

  const [fullBy, setFullBy] = useState('');
  const [discountBp, setDiscountBp] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [maxInstallments, setMaxInstallments] = useState('12');
  const [feeAmount, setFeeAmount] = useState('');

  const create = useMutation(async (body: Record<string, unknown>) =>
    api<{ id: string }>('/v1/assessments', { method: 'POST', body }),
  );

  const minor = parseMoney(amount, currency);

  return (
    <Card title="New Assessment">
      <ErrorBanner error={create.error} />

      <div className="row">
        <label>
          <span className="muted small">Code</span>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="CAP-2026"
            className="mono"
            style={{ maxWidth: 140 }}
          />
        </label>
        <label style={{ flex: 1 }}>
          <span className="muted small">Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Clubhouse Renovation"
          />
        </label>
        <label>
          <span className="muted small">Kind</span>
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="capital">Capital</option>
            <option value="special">Special</option>
            <option value="other">Other</option>
          </select>
        </label>
        <label>
          <span className="muted small">Credited to</span>
          <select value={treatment} onChange={(e) => setTreatment(e.target.value)}>
            <option value="equity">Members' equity</option>
            <option value="revenue">Revenue</option>
            <option value="deferred_revenue">Deferred revenue</option>
          </select>
        </label>
      </div>

      <div className="row" style={{ marginTop: 10 }}>
        <label>
          <span className="muted small">Levy date</span>
          <input type="date" value={levyDate} onChange={(e) => setLevyDate(e.target.value)} />
        </label>
        <label>
          <span className="muted small">Amount per member</span>
          <input value={amount} onChange={(e) => setAmount(e.target.value)} />
        </label>
      </div>

      <p className="muted small" style={{ marginTop: 6 }}>
        {treatment === 'equity'
          ? 'Members are buying a larger share of the club. Credited to equity, so it never appears as revenue.'
          : treatment === 'revenue'
            ? 'Earned when levied. Appears in this year’s surplus.'
            : 'Earned over time. Held as a liability and released month by month.'}
      </p>

      <h3 style={{ marginTop: 20, marginBottom: 8, fontSize: 14 }}>Terms</h3>

      <div className="row">
        <label>
          <span className="muted small">Settle in full by</span>
          <input type="date" value={fullBy} onChange={(e) => setFullBy(e.target.value)} />
        </label>
        <label>
          <span className="muted small">Discount %</span>
          <input
            value={discountBp}
            onChange={(e) => setDiscountBp(e.target.value)}
            placeholder="3"
            style={{ maxWidth: 90 }}
          />
        </label>
        <label>
          <span className="muted small">or club credit</span>
          <input
            value={creditAmount}
            onChange={(e) => setCreditAmount(e.target.value)}
            placeholder="250.00"
            style={{ maxWidth: 130 }}
          />
        </label>
        <label>
          <span className="muted small">Max installments</span>
          <input
            value={maxInstallments}
            onChange={(e) => setMaxInstallments(e.target.value)}
            style={{ maxWidth: 90 }}
          />
        </label>
        <label>
          <span className="muted small">Fee per installment</span>
          <input
            value={feeAmount}
            onChange={(e) => setFeeAmount(e.target.value)}
            placeholder="15.00"
            style={{ maxWidth: 130 }}
          />
        </label>
      </div>

      <p className="muted small" style={{ marginTop: 8 }}>
        A discount is revenue the club chooses not to earn; a club credit is an expense the
        member spends on food and drink. Same effect on what they pay, different story on the
        P&amp;L — the board will want them apart.
      </p>

      <div className="row" style={{ marginTop: 12 }}>
        <button
          className="gold"
          disabled={create.busy || !code || !name || minor === null}
          onClick={async () => {
            const r = await create.run({
              code,
              name,
              kind,
              treatment,
              levy_date: levyDate,
              amount_minor: minor,
              ...(fullBy ? { full_payment_by: fullBy } : {}),
              ...(discountBp ? { full_discount_bp: Math.round(Number(discountBp) * 100) } : {}),
              ...(creditAmount
                ? { full_credit_minor: parseMoney(creditAmount, currency) ?? 0 }
                : {}),
              ...(maxInstallments ? { max_installments: Number(maxInstallments) } : {}),
              ...(feeAmount ? { installment_fee_minor: parseMoney(feeAmount, currency) ?? 0 } : {}),
            });
            if (r) onDone();
          }}
        >
          {create.busy ? 'Calculating…' : 'Create and preview'}
        </button>
        <button className="secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------------ */

interface AssessmentDetail {
  assessment: {
    id: string;
    code: string;
    name: string;
    kind: string;
    treatment: string;
    status: string;
    levy_date: string;
    due_date: string | null;
    total_minor: number;
    member_count: number;
    full_payment_by: string | null;
    full_discount_bp: number;
    full_credit_minor: number;
    max_installments: number;
    installment_fee_minor: number;
  };
  amounts: { id: string; category_id: string | null; amount_minor: number; name: string | null }[];
  items: {
    id: string;
    member_account_id: string;
    account_number: string;
    display_name: string;
    amount_minor: number;
    skip_reason: string | null;
    outstanding_minor: number;
    plan_id: string | null;
    installments: number | null;
  }[];
}

export function AssessmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { currency, atLeast } = useSession();
  const detail = useApi<AssessmentDetail>(id ? `/v1/assessments/${id}` : null);
  const [acting, setActing] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const commit = useMutation(async () =>
    api(`/v1/assessments/${id}/commit`, { method: 'POST', body: {} }),
  );
  const plan = useMutation(async (body: Record<string, unknown>) =>
    api<{ note: string }>(`/v1/assessments/${id}/plans`, { method: 'POST', body }),
  );
  const incentive = useMutation(async (body: Record<string, unknown>) =>
    api(`/v1/assessments/${id}/incentive`, { method: 'POST', body }),
  );

  if (detail.loading) return <Loading rows={6} />;
  if (detail.error) return <ErrorBanner error={detail.error} />;
  const d = detail.data;
  if (!d) return null;

  const a = d.assessment;
  const levied = a.status === 'committed';
  const priced = d.items.filter((i) => i.skip_reason === null);

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        <div>
          <h1>{a.name}</h1>
          <div className="muted small mono">
            {a.code} · levied {date(a.levy_date)}
          </div>
        </div>
        <div className="spacer" />
        {!levied && a.status !== 'reversed' && atLeast('controller') && (
          <button
            className="gold"
            disabled={commit.busy}
            onClick={async () => {
              const r = await commit.run();
              if (r) detail.reload();
            }}
          >
            {commit.busy ? 'Levying…' : `Levy ${money(a.total_minor, currency)} to ${a.member_count} members`}
          </button>
        )}
      </div>

      <ErrorBanner error={commit.error ?? plan.error ?? incentive.error} />
      {note && (
        <Card>
          <Status tone="neutral">{note}</Status>
        </Card>
      )}

      <div className="tiles">
        <Tile label="Total" value={money(a.total_minor, currency)} />
        <Tile label="Members" value={String(a.member_count)} />
        <Tile
          label="Status"
          value={levied ? 'Levied' : titleCase(a.status)}
          tone={levied ? 'good' : 'warning'}
          note={levied ? `on ${date(a.levy_date)}` : 'nothing charged yet'}
        />
        <Tile
          label="Settle in full"
          value={
            a.full_discount_bp
              ? `${(a.full_discount_bp / 100).toFixed(a.full_discount_bp % 100 ? 2 : 0)}% off`
              : a.full_credit_minor
                ? money(a.full_credit_minor, currency)
                : '—'
          }
          note={a.full_payment_by ? `by ${date(a.full_payment_by)}` : 'no deadline set'}
        />
      </div>

      <Card title={levied ? 'Who was levied' : 'Who would be levied'} flush>
        <table className="data">
          <thead>
            <tr>
              <th>Account</th>
              <th>Member</th>
              <th className="num">Assessed</th>
              <th className="num">Outstanding</th>
              <th>Paying</th>
              {levied && atLeast('manager') && <th />}
            </tr>
          </thead>
          <tbody>
            {priced.map((i) => (
              <tr key={i.id}>
                <td className="mono">{i.account_number}</td>
                <td>
                  <Link to={`/members/${i.member_account_id}`}>{i.display_name}</Link>
                </td>
                <td className="num">{money(i.amount_minor, currency)}</td>
                <td className="num">
                  {levied ? <Money minor={i.outstanding_minor} currency={currency} /> : '—'}
                </td>
                <td className="muted">
                  {/*
                    "in full" was wrong here: it read as a claim about how
                    they had paid, on a row where nothing had been paid at
                    all. Absence of a plan is absence of terms, not
                    settlement.
                  */}
                  {i.plan_id
                    ? `${i.installments} installments`
                    : !levied
                      ? ''
                      : i.outstanding_minor === 0
                        ? 'Settled'
                        : '—'}
                </td>
                {levied && atLeast('manager') && (
                  <td className="num" style={{ whiteSpace: 'nowrap' }}>
                    {acting === i.id ? (
                      <PlanChooser
                        max={a.max_installments}
                        busy={plan.busy}
                        onChoose={async (n) => {
                          const r = await plan.run({
                            member_account_id: i.member_account_id,
                            installments: n,
                          });
                          if (r) {
                            setNote(r.note);
                            setActing(null);
                            detail.reload();
                          }
                        }}
                        onCancel={() => setActing(null)}
                      />
                    ) : (
                      <>
                        {!i.plan_id && i.outstanding_minor > 0 && (
                          <button className="secondary small" onClick={() => setActing(i.id)}>
                            Spread it
                          </button>
                        )}{' '}
                        {!i.plan_id && a.full_discount_bp > 0 && (
                          <button
                            className="secondary small"
                            disabled={incentive.busy}
                            onClick={async () => {
                              const r = await incentive.run({
                                member_account_id: i.member_account_id,
                                kind: 'discount',
                              });
                              if (r) detail.reload();
                            }}
                          >
                            Discount
                          </button>
                        )}{' '}
                        {!i.plan_id && a.full_credit_minor > 0 && (
                          <button
                            className="secondary small"
                            disabled={incentive.busy}
                            onClick={async () => {
                              const r = await incentive.run({
                                member_account_id: i.member_account_id,
                                kind: 'credit',
                              });
                              if (r) detail.reload();
                            }}
                          >
                            Club credit
                          </button>
                        )}
                      </>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {d.items.some((i) => i.skip_reason) && (
        <Card title="Not Levied, and Why">
          <table className="data">
            <tbody>
              {d.items
                .filter((i) => i.skip_reason)
                .map((i) => (
                  <tr key={i.id}>
                    <td className="mono">{i.account_number}</td>
                    <td>{i.display_name}</td>
                    <td className="muted">{i.skip_reason}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </Card>
      )}
    </>
  );
}

function PlanChooser({
  max,
  busy,
  onChoose,
  onCancel,
}: {
  max: number;
  busy: boolean;
  onChoose: (n: number) => void;
  onCancel: () => void;
}) {
  const [n, setN] = useState(String(Math.min(12, max)));
  return (
    <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      <input
        value={n}
        onChange={(e) => setN(e.target.value)}
        style={{ width: 60 }}
        aria-label="Installments"
      />
      <span className="muted small">of {max}</span>
      <button className="small" disabled={busy} onClick={() => onChoose(Number(n))}>
        {busy ? '…' : 'Set'}
      </button>
      <button className="secondary small" onClick={onCancel}>
        ✕
      </button>
    </span>
  );
}
